import React from 'react';
import { ChevronRight, Briefcase, Calendar } from 'lucide-react';

import { Card, CardContent } from '@/Components/ui/card';
import { Button } from '@/Components/ui/button';
import { Badge } from '@/Components/ui/badge';
import { cn } from '@/lib/utils';
// "Uploaded by …" chip — for the recruiter list view, the viewer is
// the recruiter so the badge shows "Myself" for their own uploads,
// "Company" when the employer added a candidate to their assigned job,
import UploadSourceBadge from '@/Components/UploadSourceBadge';

/**
 * Compact candidate card for the Applications page list.
 *
 * Props:
 * - candidate: { id, name, jobTitle, status, submittedAt, ... }
 * - onViewProfile: function(candidate)
 */
export default function CandidateCard({
  candidate = {},
  onViewProfile = () => {},
}) {
  const name = candidate.name || 'Anonymous';
  const initials = name
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const submitted = candidate.submittedAt
    ? new Date(candidate.submittedAt).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : 'N/A';

  const status = (candidate.status || 'Submitted').toString();

  // Status → color tone (only style; status text comes straight from API).
  const statusTone = {
    hired:
      'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400',
    selected: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400',
    shortlisted:
      'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-400',
    submitted:
      'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
    rejected: 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400',
  }[status.toLowerCase()] ||
    'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';

  return (
    <Card className="border border-border/40 transition-all hover:border-border/80 hover:shadow-sm">
      <CardContent className="flex items-center gap-4 p-4">
        {/* Avatar */}
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
          {initials}
        </div>

        {/* Identity */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium truncate">{name}</span>
            {/* Recruiter view of "Uploaded by". When the recruiter sees
                their own upload it renders "Myself"; when the company
                or an admin added the candidate to their job, recruiter
                sees that role label so they know the source. */}
            <UploadSourceBadge
              uploadedByEmail={candidate.uploaded_by_email}
              uploadedByType={candidate.uploaded_by_type}
              uploadedByCompanyName={candidate.uploaded_by_company_name}
              currentViewerEmail={
                sessionStorage.getItem('recruiter_email')
                || localStorage.getItem('recruiter_email')
                || ''
              }
              currentViewerType="recruiter"
              variant="compact"
            />
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            {candidate.jobTitle && (
              <span className="flex items-center gap-1">
                <Briefcase className="h-3 w-3" />
                <span className="truncate">{candidate.jobTitle}</span>
              </span>
            )}
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              <span>{submitted}</span>
            </span>
          </div>
        </div>

        {/* Status */}
        <Badge
          variant="outline"
          className={cn(
            'border-0 px-2 py-1 text-xs font-medium uppercase',
            statusTone,
          )}
        >
          {status}
        </Badge>

        {/* Action */}
        <Button
          size="sm"
          variant="outline"
          onClick={() => onViewProfile(candidate)}
        >
          View Profile
          <ChevronRight className="ml-1 h-3.5 w-3.5" />
        </Button>
      </CardContent>
    </Card>
  );
}
